import {
    getGrn,
    getStoreCatalog,
    listShelfLocations,
    reverseStoreGrn,
    submitGrnPutaway,
    submitGrnQc,
    type Grn,
    type ShelfLocation,
    type ShelfAssignmentInput,
} from '@api/warehouse-ops';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import {
    ActionIcon,
    Alert,
    Button,
    Divider,
    Group,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
    ThemeIcon,
    Tooltip,
} from '@mantine/core';
import {
    CheckCircle,
    Circle,
    Info,
    Plus,
    Trash,
    Warning,
    X,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FC } from 'react';
import { listWarehousesForPicker } from '@api/work-order';
import { reasonText } from '@components/containers/vendorHistory/ledgerRows';

import GrnStatusBadge from './GrnStatusBadge';

// ─── Material GRN (received into a store) ────────────────────────────────────

/** Where the rejected quantity went, from the Action picked at the gate. */
const REJECTION_OUTCOME: Record<string, string> = {
    RM_RETURN: 'Returned to supplier',
    WASTAGE: 'Scrapped',
    MARKED_BAD: 'Kept at site',
};

type MaterialGrnRow = GrnItem & { name: string; outcome: string; why: string };

/**
 * A raw and packaging material GRN. QC was the gate's verdict when it was received, so there is
 * no QC or putaway step to walk through: the lines as a table, and an undo.
 */
const MaterialGrnBody: FC<{ grn: Grn; onReversed: () => void }> = ({ grn, onReversed }) => {
    const [reason, setReason] = useState('');
    const [confirming, setConfirming] = useState(false);
    const { data: catalog } = useQuery(['store-catalog', grn.sourceWarehouseId], () =>
        getStoreCatalog(grn.sourceWarehouseId),
    );
    const materialById = new Map((catalog?.materials ?? []).map((m) => [m.itemId, m]));

    const rows: MaterialGrnRow[] = (grn.items ?? []).map((item) => ({
        ...item,
        name: materialById.get(item.itemId)?.name ?? item.itemId,
        outcome: (item.qcFailedQty ?? 0) > 0 ? (REJECTION_OUTCOME[item.rejection?.movementType ?? ''] ?? 'Rejected') : '',
        why: item.rejection ? (reasonText({ reasonCode: item.rejection.reasonCode, notes: item.rejection.notes }) ?? '') : '',
    }));
    const qty = (value: number | null | undefined, row: MaterialGrnRow) => {
        const unit = materialById.get(row.itemId)?.unit;
        return value == null ? '—' : `${value}${unit ? ` ${unit}` : ''}`;
    };
    const columns: DataSheetColumn<MaterialGrnRow>[] = [
        { key: 'name', header: 'Item', width: 240, sticky: true },
        { key: 'orderedQty', header: 'Ordered', width: 100, align: 'right', render: (v: number, r) => qty(v, r) },
        { key: 'receivedQty', header: 'Received', width: 100, align: 'right', render: (v: number, r) => qty(v, r) },
        { key: 'qcPassedQty', header: 'Usable', width: 100, align: 'right', render: (v: number | null, r) => qty(v ?? r.receivedQty, r) },
        {
            key: 'qcFailedQty',
            header: 'Rejected',
            width: 100,
            align: 'right',
            render: (v: number | null, r) => ((v ?? 0) > 0 ? qty(v, r) : '—'),
        },
        { key: 'outcome', header: 'What happened', width: 170, render: (v: string) => v || '—' },
        { key: 'why', header: 'Reason', width: 220, render: (v: string) => v || '—' },
    ];

    const reverse = useMutation({
        mutationFn: () => reverseStoreGrn(grn.id, reason.trim()),
        onSuccess: () => {
            setConfirming(false);
            onReversed();
        },
    });

    return (
        <Stack gap="md" p="lg">
            <Text size="sm" c="dimmed">
                {[
                    grn.sourceId && `${grn.sourceType === 'STN' ? 'STN' : 'PO'} ${grn.sourceLabel ?? grn.sourceId}`,
                    `Received ${grn.receivedDate}`,
                    grn.receivedBy && `by ${grn.receivedBy}`,
                ]
                    .filter(Boolean)
                    .join(' · ')}
            </Text>
            <DataSheet<MaterialGrnRow>
                data={rows}
                columns={columns}
                getRowKey={(r) => r.id}
                hideHeaderBar
                maxHeight="auto"
            />

            {grn.reversed ? (
                <Alert color="red" variant="light">This GRN was reversed. Its stock is back off the books.</Alert>
            ) : confirming ? (
                <Stack gap="sm">
                    <Textarea
                        label="Why is this being reversed?"
                        value={reason}
                        onChange={(e) => setReason(e.currentTarget.value)}
                        autosize
                        minRows={2}
                    />
                    {reverse.isError && (
                        <Alert color="red" variant="light">
                            {(reverse.error as any)?.error?.message ?? (reverse.error as Error)?.message ?? 'Could not reverse it.'}
                        </Alert>
                    )}
                    <Group justify="flex-end" gap="sm">
                        <Button variant="default" onClick={() => setConfirming(false)}>Cancel</Button>
                        <Button
                            color="red"
                            disabled={!reason.trim()}
                            loading={reverse.isLoading}
                            onClick={() => reverse.mutate()}
                        >
                            Reverse GRN
                        </Button>
                    </Group>
                </Stack>
            ) : (
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setConfirming(true)}>Reverse</Button>
                </Group>
            )}
        </Stack>
    );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GrnItem = NonNullable<Grn['items']>[number];

type QcRow = { grnItemId: string; itemId: string; receivedQty: number; passedQty: number; failedQty: number };
type ShelfRow = { shelfLocationId: string; quantity: number };
type PutawayRow = { grnItemId: string; itemId: string; qcPassedQty: number; assignments: ShelfRow[] };

const STATUS_LABEL: Record<string, string> = {
    RECEIVED: 'Created',
    QC_DONE: 'QC Complete',
    PUTAWAY_DONE: 'Putaway Complete',
};

// ─── Milestone step indicator ─────────────────────────────────────────────────

const Step: FC<{ done: boolean; active: boolean; label: string; timestamp?: string | null }> = ({
    done, active, label, timestamp,
}) => (
    <Group gap="sm" align="flex-start">
        <ThemeIcon
            size="md"
            radius="xl"
            color={done ? 'green' : active ? 'blue' : 'gray'}
            variant={done || active ? 'filled' : 'light'}
        >
            {done ? <CheckCircle size={14} /> : <Circle size={14} />}
        </ThemeIcon>
        <Stack gap={0}>
            <Text size="sm" fw={600}>{label}</Text>
            {timestamp && (
                <Text size="xs" c="dimmed">{new Date(timestamp).toLocaleString()}</Text>
            )}
        </Stack>
    </Group>
);

// ─── Step 1: Items table ──────────────────────────────────────────────────────

const GrnItemsTable: FC<{ items: GrnItem[]; grn: Grn }> = ({ items, grn }) => {
    const cols: DataSheetColumn<GrnItem>[] = [
        { key: 'itemId', header: 'Item ID', width: 180, sticky: true },
        { key: 'orderedQty', header: 'Ordered', width: 100, align: 'right', render: (_, r) => r.orderedQty },
        { key: 'receivedQty', header: 'Received', width: 100, align: 'right', render: (_, r) => r.receivedQty },
        {
            key: 'variance', header: 'Variance', width: 90, align: 'right',
            render: (_, r) => {
                const v = r.receivedQty - r.orderedQty;
                return <Text size="sm" c={v === 0 ? undefined : v > 0 ? 'green' : 'orange'}>{v > 0 ? '+' : ''}{v}</Text>;
            },
        },
    ];

    return (
        <Stack gap="sm">
            <Group gap="lg" wrap="wrap">
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Source</Text>
                    <Text size="sm" fw={500}>{grn.sourceType.replace('_', ' ')}: {grn.sourceLabel ?? grn.sourceId ?? '—'}</Text>
                </Stack>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Receiving Warehouse</Text>
                    <Text size="sm" fw={500}>{grn.sourceWarehouseId}</Text>
                </Stack>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Received Date</Text>
                    <Text size="sm" fw={500}>{grn.receivedDate}</Text>
                </Stack>
                <Stack gap={2}>
                    <Text size="xs" c="dimmed">Created by</Text>
                    <Text size="sm" fw={500}>{grn.createdBy}</Text>
                </Stack>
                {grn.receivedBy && (
                    <Stack gap={2}>
                        <Text size="xs" c="dimmed">Received by</Text>
                        <Text size="sm" fw={500}>{grn.receivedBy}</Text>
                    </Stack>
                )}
                {grn.deliveryNote && (
                    <Stack gap={2}>
                        <Text size="xs" c="dimmed">Delivery note</Text>
                        <Text size="sm" fw={500}>{grn.deliveryNote}</Text>
                    </Stack>
                )}
            </Group>
            <DataSheet<GrnItem>
                data={items}
                columns={cols}
                getRowKey={(r) => r.id}
                hideHeaderBar
                maxHeight="auto"
            />
        </Stack>
    );
};

// ─── Step 2: QC panel ─────────────────────────────────────────────────────────

const QcPanel: FC<{ grn: Grn; onDone: () => void }> = ({ grn, onDone }) => {
    const queryClient = useQueryClient();
    const items = grn.items ?? [];

    const [rows, setRows] = useState<QcRow[]>(
        items.map((i) => ({
            grnItemId: i.id,
            itemId: i.itemId,
            receivedQty: i.receivedQty,
            passedQty: i.qcPassedQty ?? i.receivedQty,
            failedQty: i.qcFailedQty ?? 0,
        })),
    );

    const updatePassed = (id: string, val: number) =>
        setRows((prev) => prev.map((r) => r.grnItemId === id
            ? { ...r, passedQty: val, failedQty: Math.max(0, r.receivedQty - val) }
            : r,
        ));

    const updateFailed = (id: string, val: number) =>
        setRows((prev) => prev.map((r) => r.grnItemId === id
            ? { ...r, failedQty: val, passedQty: Math.max(0, r.receivedQty - val) }
            : r,
        ));

    const invalid = rows.filter((r) =>
        Math.round((r.passedQty + r.failedQty) * 1000) !== Math.round(r.receivedQty * 1000),
    );

    const mutation = useMutation({
        mutationFn: () =>
            submitGrnQc(grn.id, {
                items: rows.map((r) => ({
                    grnItemId: r.grnItemId,
                    qcPassedQty: r.passedQty,
                    qcFailedQty: r.failedQty,
                })),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['grn-panel', grn.id] });
            queryClient.invalidateQueries({ queryKey: ['grns'] });
            onDone();
        },
    });

    const totalFailed = rows.reduce((s, r) => s + r.failedQty, 0);

    return (
        <Stack gap="sm">
            {rows.map((row) => {
                const isInvalid = Math.round((row.passedQty + row.failedQty) * 1000) !== Math.round(row.receivedQty * 1000);
                return (
                    <Group key={row.grnItemId} gap="sm" align="flex-end" wrap="wrap">
                        <Stack gap={2} style={{ minWidth: 120, flex: 1 }}>
                            <Text size="sm" fw={500}>{row.itemId}</Text>
                            <Text size="xs" c="dimmed">Received: {row.receivedQty}</Text>
                        </Stack>
                        <NumberInput
                            w={100} label="Passed" min={0} max={row.receivedQty}
                            value={row.passedQty}
                            onChange={(v) => updatePassed(row.grnItemId, Number(v) || 0)}
                            styles={isInvalid ? { input: { borderColor: 'var(--mantine-color-red-5)' } } : undefined}
                        />
                        <NumberInput
                            w={100} label="Failed" min={0} max={row.receivedQty}
                            value={row.failedQty}
                            onChange={(v) => updateFailed(row.grnItemId, Number(v) || 0)}
                            styles={isInvalid ? { input: { borderColor: 'var(--mantine-color-red-5)' } } : undefined}
                        />
                        {isInvalid && <Text size="xs" c="red" mt={18}>≠ {row.receivedQty}</Text>}
                    </Group>
                );
            })}
            {totalFailed > 0 && (
                <Alert icon={<Warning size={14} />} color="yellow">
                    {totalFailed} unit(s) failed QC — coordinate return with vendor separately.
                </Alert>
            )}
            <Group justify="flex-end">
                <Button
                    size="sm"
                    disabled={invalid.length > 0}
                    loading={mutation.isPending}
                    onClick={() => mutation.mutate()}
                >
                    Submit QC
                </Button>
            </Group>
        </Stack>
    );
};

// ─── Step 3: Putaway panel ────────────────────────────────────────────────────

const PutawayPanel: FC<{ grn: Grn; onDone: () => void }> = ({ grn, onDone }) => {
    const queryClient = useQueryClient();
    const items = (grn.items ?? []).filter((i) => (i.qcPassedQty ?? 0) > 0);

    const shelvesQuery = useQuery<ShelfLocation[]>({
        queryKey: ['shelf-locations', grn.sourceWarehouseId],
        queryFn: () => listShelfLocations(grn.sourceWarehouseId),
    });
    const shelfOptions = (shelvesQuery.data ?? []).map((s) => ({
        value: s.id,
        label: `${s.code}${s.name ? ` — ${s.name}` : ''}`,
    }));

    const [rows, setRows] = useState<PutawayRow[]>(
        items.map((i) => ({
            grnItemId: i.id,
            itemId: i.itemId,
            qcPassedQty: i.qcPassedQty ?? 0,
            assignments: [{ shelfLocationId: '', quantity: i.qcPassedQty ?? 0 }],
        })),
    );

    const addAssignment = (grnItemId: string) =>
        setRows((prev) => prev.map((r) => r.grnItemId === grnItemId
            ? { ...r, assignments: [...r.assignments, { shelfLocationId: '', quantity: 0 }] }
            : r,
        ));

    const removeAssignment = (grnItemId: string, idx: number) =>
        setRows((prev) => prev.map((r) => r.grnItemId === grnItemId
            ? { ...r, assignments: r.assignments.filter((_, i) => i !== idx) }
            : r,
        ));

    const updateAssignment = (grnItemId: string, idx: number, field: keyof ShelfRow, value: string | number) =>
        setRows((prev) => prev.map((r) => r.grnItemId === grnItemId
            ? { ...r, assignments: r.assignments.map((a, i) => i === idx ? { ...a, [field]: value } : a) }
            : r,
        ));

    const mutation = useMutation({
        mutationFn: () =>
            submitGrnPutaway(grn.id, {
                items: rows.map((r) => ({
                    grnItemId: r.grnItemId,
                    assignments: r.assignments.filter((a) => a.shelfLocationId && a.quantity > 0),
                })),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['grn-panel', grn.id] });
            queryClient.invalidateQueries({ queryKey: ['grns'] });
            onDone();
        },
    });

    const isStnSource = grn.sourceType === 'STN';

    return (
        <Stack gap="md">
            {isStnSource && (
                <Alert icon={<Info size={14} />} color="blue">
                    Stock was already credited when the STN was received. Putaway records shelf placement only.
                </Alert>
            )}
            {rows.map((row) => {
                const totalAssigned = row.assignments.reduce((s, a) => s + a.quantity, 0);
                const over = totalAssigned > row.qcPassedQty;
                return (
                    <Stack key={row.grnItemId} gap="xs">
                        <Group justify="space-between">
                            <Text size="sm" fw={600}>{row.itemId}</Text>
                            <Text size="xs" c={over ? 'red' : 'dimmed'}>
                                {totalAssigned} / {row.qcPassedQty} assigned{over ? ' (exceeds passed qty!)' : ''}
                            </Text>
                        </Group>
                        {row.assignments.map((assignment, idx) => (
                            <Group key={idx} gap="xs" align="flex-end" pl="sm">
                                <Select
                                    style={{ flex: 1 }}
                                    label={idx === 0 ? 'Shelf' : undefined}
                                    placeholder="Select shelf"
                                    data={shelfOptions}
                                    value={assignment.shelfLocationId || null}
                                    onChange={(v) => updateAssignment(row.grnItemId, idx, 'shelfLocationId', v ?? '')}
                                    searchable
                                    clearable
                                />
                                <NumberInput
                                    w={90}
                                    label={idx === 0 ? 'Qty' : undefined}
                                    min={0}
                                    value={assignment.quantity}
                                    onChange={(v) => updateAssignment(row.grnItemId, idx, 'quantity', Number(v) || 0)}
                                />
                                {row.assignments.length > 1 && (
                                    <ActionIcon
                                        color="red" variant="subtle" size="sm"
                                        mb={2}
                                        onClick={() => removeAssignment(row.grnItemId, idx)}
                                    >
                                        <Trash size={14} />
                                    </ActionIcon>
                                )}
                            </Group>
                        ))}
                        <Button
                            size="xs" variant="subtle"
                            leftSection={<Plus size={12} />}
                            w="fit-content" pl="sm"
                            onClick={() => addAssignment(row.grnItemId)}
                        >
                            Add shelf
                        </Button>
                        <Divider />
                    </Stack>
                );
            })}
            <Group justify="flex-end">
                <Button
                    size="sm"
                    loading={mutation.isPending}
                    onClick={() => mutation.mutate()}
                >
                    Complete Putaway
                </Button>
            </Group>
        </Stack>
    );
};

// ─── Completed putaway summary ────────────────────────────────────────────────

const PutawaySummary: FC<{ items: GrnItem[] }> = ({ items }) => (
    <Stack gap="xs">
        {items.map((item) => (
            <Stack key={item.id} gap={4}>
                <Text size="sm" fw={600}>{item.itemId} — {item.putawayQty} units</Text>
                {(item.shelfAssignments ?? []).map((a) => (
                    <Group key={a.id} pl="md" gap="sm">
                        <Text size="xs" c="dimmed">Shelf {a.shelfLocationId}</Text>
                        <Text size="xs">{a.quantity} units</Text>
                        <Text size="xs" c="dimmed">— {new Date(a.createdAt).toLocaleString()}</Text>
                    </Group>
                ))}
            </Stack>
        ))}
    </Stack>
);

// ─── Main panel ───────────────────────────────────────────────────────────────

type Props = { grnId: string; onClose: () => void };

const GrnDetailPanel: FC<Props> = ({ grnId, onClose }) => {
    const [activeStep, setActiveStep] = useState<0 | 1 | 2>(0);

    const query = useQuery({
        queryKey: ['grn-panel', grnId],
        queryFn: () => getGrn(grnId),
    });

    const grn = query.data;
    const queryClient = useQueryClient();

    const step = grn
        ? grn.status === 'PUTAWAY_DONE' ? 2
        : grn.status === 'QC_DONE' ? 1
        : 0
        : 0;

    const displayStep = Math.max(step, activeStep);

    if (!grn) {
        return (
            <div className="fixed inset-0 z-30 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm">
                <div className="relative flex h-full w-full max-w-2xl flex-col border-l border-slate-200 bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                        <Text size="sm" c="dimmed">Loading...</Text>
                        <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                            <X size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-30 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm">
            <div
                className={`relative flex h-full w-full ${grn.itemType === 'material' ? 'max-w-5xl' : 'max-w-2xl'} flex-col border-l border-slate-200 bg-white shadow-2xl`}
            >
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <Group gap="sm">
                            <Text size="lg" fw={700}>{grn.code}</Text>
                            <GrnStatusBadge status={grn.status} reversed={grn.reversed} itemType={grn.itemType} label={STATUS_LABEL[grn.status]} />
                        </Group>
                        <Text size="xs" c="dimmed" mt={2}>
                            {grn.itemCount ?? grn.items?.length ?? 0} item(s) · {grn.sourceType.replace('_', ' ')}
                        </Text>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100">
                        <X size={18} />
                    </button>
                </div>

                {grn.itemType === 'material' ? (
                    <div className="flex flex-1 flex-col overflow-y-auto">
                        <MaterialGrnBody
                            grn={grn}
                            onReversed={() => {
                                void query.refetch();
                                void queryClient.invalidateQueries(['grns']);
                            }}
                        />
                    </div>
                ) : (
                /* Scrollable body */
                <div className="flex flex-1 flex-col gap-0 overflow-y-auto">
                    {/* Timeline */}
                    <div className="border-b border-slate-100 px-6 py-4">
                        <Group gap="xl">
                            <button onClick={() => setActiveStep(0)} className="text-left">
                                <Step done={step >= 1} active={displayStep === 0} label="GRN Created" timestamp={grn.createdAt} />
                            </button>
                            <div className="h-px flex-1 bg-slate-200 mt-3" />
                            <button onClick={() => step >= 1 && setActiveStep(1)} className="text-left">
                                <Step done={step >= 2} active={displayStep === 1} label="QC Complete" timestamp={grn.qcCompletedAt} />
                            </button>
                            <div className="h-px flex-1 bg-slate-200 mt-3" />
                            <button onClick={() => step >= 2 && setActiveStep(2)} className="text-left">
                                <Step done={step >= 2} active={displayStep === 2} label="Putaway Done" timestamp={grn.putawayCompletedAt} />
                            </button>
                        </Group>
                    </div>

                    {/* Step content */}
                    <div className="px-6 py-5">
                        {displayStep === 0 && (
                            <Stack gap="md">
                                <GrnItemsTable items={grn.items ?? []} grn={grn} />
                                {grn.status === 'RECEIVED' && (
                                    <Group justify="flex-end">
                                        <Button size="sm" onClick={() => setActiveStep(1)}>
                                            Start QC →
                                        </Button>
                                    </Group>
                                )}
                            </Stack>
                        )}

                        {displayStep === 1 && (
                            grn.status === 'RECEIVED'
                                ? <QcPanel grn={grn} onDone={() => setActiveStep(1)} />
                                : (
                                    <Stack gap="sm">
                                        <Text size="sm" fw={500} c="dimmed">QC Results</Text>
                                        {(grn.items ?? []).map((i) => (
                                            <Group key={i.id} justify="space-between">
                                                <Text size="sm">{i.itemId}</Text>
                                                <Group gap="md">
                                                    <Text size="sm" c="green">✓ {i.qcPassedQty} passed</Text>
                                                    {(i.qcFailedQty ?? 0) > 0 && (
                                                        <Text size="sm" c="red">✗ {i.qcFailedQty} failed</Text>
                                                    )}
                                                </Group>
                                            </Group>
                                        ))}
                                        {grn.status === 'QC_DONE' && (
                                            <Group justify="flex-end" mt="sm">
                                                <Button size="sm" onClick={() => setActiveStep(2)}>
                                                    Start Putaway →
                                                </Button>
                                            </Group>
                                        )}
                                    </Stack>
                                )
                        )}

                        {displayStep === 2 && (
                            grn.status === 'QC_DONE'
                                ? <PutawayPanel grn={grn} onDone={() => {}} />
                                : <PutawaySummary items={grn.items ?? []} />
                        )}
                    </div>
                </div>
                )}
            </div>
        </div>
    );
};

export default GrnDetailPanel;
